import { isGifUrl } from "@/lib/gif-utils";

function decodeHtml(value: string): string {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
}

function extractUrls(value: string): string[] {
  return Array.from(value.matchAll(/https?:\/\/[^\s"'<>\\]+/gi), (match) =>
    decodeHtml(match[0]).replace(/[),.;]+$/, "")
  );
}

function unwrapGoogleImageUrl(value: string): string {
  try {
    const parsed = new URL(value);
    const imgUrl = parsed.searchParams.get("imgurl");
    return imgUrl ? decodeURIComponent(imgUrl) : value;
  } catch {
    return value;
  }
}

function getClipboardGifUrl(data: DataTransfer): string | null {
  const html = data.getData("text/html");
  const plainText = data.getData("text/plain");
  const uriList = data.getData("text/uri-list");
  const candidates = new Set<string>();

  if (html) {
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      doc.querySelectorAll("img, a, source").forEach((element) => {
        ["src", "href", "data-src", "data-original", "data-iurl"].forEach((attr) => {
          const value = element.getAttribute(attr);
          if (value) candidates.add(decodeHtml(value));
        });
      });
    } catch {
      // Fall through to regex extraction below.
    }
    extractUrls(html).forEach((url) => candidates.add(url));
  }

  [plainText, uriList].forEach((value) => {
    if (value) extractUrls(value).forEach((url) => candidates.add(url));
  });

  for (const candidate of candidates) {
    const unwrapped = unwrapGoogleImageUrl(candidate);
    if (isGifUrl(unwrapped)) return unwrapped;
  }

  return null;
}

export function hasPastedImageData(data: DataTransfer): boolean {
  return Array.from(data.items).some(
    (item) => item.kind === "file" && item.type.startsWith("image/")
  );
}

async function fetchGifFile(url: string): Promise<File | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!blob.type.startsWith("image/")) return null;
    return new File([blob], `pasted-gif-${Date.now()}.gif`, {
      type: blob.type || "image/gif",
    });
  } catch {
    return null;
  }
}

export async function getPastedImageFiles(data: DataTransfer): Promise<File[]> {
  const pastedImages = Array.from(data.items)
    .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
    .map((item) => item.getAsFile())
    .filter((file): file is File => file != null);

  const gifUrl = getClipboardGifUrl(data);
  if (!gifUrl) return pastedImages;

  const gifFile = await fetchGifFile(gifUrl);
  return gifFile ? [gifFile] : pastedImages;
}
